import { t } from '@lingui/macro';
import {
  ErrorCodeFromApi,
  ErrorMsgToDisplayFn,
  ErrorPropertyToDisplayFn,
  PegasusAdvancedTimeAPIModel,
  PegasusAdvancedTimeHolidayAPIModel,
  PegasusAdvancedTimeHolidayModel,
  PegasusAdvancedTimeModel,
  PegasusFlowStatusesAPIModel,
  PegasusFlowStatusesModel,
  PegasusPredefinedFlowPutModel,
  PegasusPredefinedSingleTablesModel,
  PegasusPredefinedTablesAPIModel,
  PegasusPredefinedTablesModel,
  PegasusPredefinedTimePutModel,
  PegasusTimeStatusesAPIModel,
  PegasusTimeStatusesModel,
  PMZoneUnitsState,
  SettingsFormErrorModel,
} from '@shared/models';
import { format, minutesToHours, parseJSON } from 'date-fns';
import { isEmpty } from 'lodash';
import { boolean, date, number, object, ref, string } from 'yup';
import {
  ControlFlagsType,
  DstDayOfWeekType,
  FlowInvalidToUseType,
  ModeSettingsGetModel,
  MultipleCPsType,
  MultipleFlowTableOffsetsAPIModel,
  MultipleFlowTableOffsetsModel,
  SettingsFormModel,
  TablesAndLearningDataSource,
  TimeLearningTableType,
} from './ControlTabs.models';
import { ModeSettingsTabsType } from './ModeSettings/ModeSettings.utils';

export const initialValuesToModeAndGeneralSettings = (modeSettingsData: ModeSettingsGetModel) => {
  return {
    // prv / cp control
    prvId: modeSettingsData.prvId,
    cpId: modeSettingsData.cpId,
    cpChannelId: modeSettingsData.cpChannelId,
    cp2Id: modeSettingsData.cp2Id,
    cp2ChannelId: modeSettingsData.cp2ChannelId,
    cp3Id: modeSettingsData.cp3Id,
    cp3ChannelId: modeSettingsData.cp3ChannelId,

    pressureUnit: modeSettingsData.pressureUnit,
    flowUnit: modeSettingsData.flowUnit,

    // note //
    note: modeSettingsData?.note ?? '',

    // mode selection //
    controlMode: modeSettingsData.controlMode ?? 'Off',
    bigTimeTableEnabled: modeSettingsData.bigTimeTableEnabled,

    // actuator limits //
    actuatorMax: modeSettingsData.actuatorMax ?? undefined,
    actuatorMin: modeSettingsData.actuatorMin ?? undefined,

    // flow section //
    useZeroFlows: modeSettingsData.useZeroFlows,
    faultTimeout: modeSettingsData.faultTimeout ?? undefined, // int
    useFlowBandLimits: modeSettingsData.useFlowBandLimits,
    minValidFlow: modeSettingsData.minValidFlow ?? undefined,
    maxValidFlow: modeSettingsData.maxValidFlow ?? undefined,
    invalidFlowOverride: modeSettingsData.invalidFlowOverride ?? FlowInvalidToUseType.UseTimeTable,
    faultPressure: modeSettingsData.faultPressure ?? undefined,

    // overrides //
    holOn: modeSettingsData.holOn,
    holStart: modeSettingsData.holStart ? parseJSON(modeSettingsData.holStart) : null, // date
    holEnd: modeSettingsData.holEnd ? parseJSON(modeSettingsData.holEnd) : null, // date

    // regular time table //
    controlFlags:
      (modeSettingsData.invalidFlowOverride ?? FlowInvalidToUseType.UseTimeTable) ===
        FlowInvalidToUseType.UseTimeTable && modeSettingsData.controlFlags === ControlFlagsType.DontUseTimeTable
        ? ControlFlagsType.UseTimeTableOnFlowErrorOnly
        : modeSettingsData.controlFlags,

    // fixed outlet //
    cpTarget: modeSettingsData.cpTarget ?? undefined,

    // cp control //
    cpMin: modeSettingsData.cpMin ?? undefined,
    maxValidCp: modeSettingsData.maxValidCp ?? undefined,
    minValidCp: modeSettingsData.minValidCp ?? undefined,
    cpBand: modeSettingsData.cpBand ?? undefined,
    // multiple cps (advanced table)
    cpMode: modeSettingsData.cpMode ?? 'Off',
    cpNightStartMins: modeSettingsData.cpNightStartMins ?? undefined, // int
    cpNightEndMins: modeSettingsData.cpNightEndMins ?? undefined, // int

    // solenoid //
    solenoidActuationThreshold: modeSettingsData.solenoidActuationThreshold ?? undefined,

    // dead band control //
    deadBand: modeSettingsData.deadBand ?? undefined,
    deadBandLimit: modeSettingsData.deadBandLimit ?? undefined,

    // single flow table offset //
    nightOffset: modeSettingsData.nightOffset ?? undefined,
    nightStartMins: parseMinsFromMidnightToTime(modeSettingsData?.nightStartMins), // hh:mm
    nightEndMins: parseMinsFromMidnightToTime(modeSettingsData?.nightEndMins), // hh:mm

    // latch mode //
    latchMode: modeSettingsData.latchMode ?? 'NoLatch',

    // controller alarms //
    maxInletPres: modeSettingsData.maxInletPres ?? undefined,
    minInletPres: modeSettingsData.minInletPres ?? undefined,
    minFlow: modeSettingsData.minFlow ?? undefined,
    maxFlow: modeSettingsData.maxFlow ?? undefined,
    minOutletPres: modeSettingsData.minOutletPres ?? undefined,
    maxOutletPres: modeSettingsData.maxOutletPres ?? undefined,
    minTopPres: modeSettingsData.minTopPres ?? undefined,
    maxTopPres: modeSettingsData.maxTopPres ?? undefined,

    // summer time offset //
    summerStart: modeSettingsData.summerStart ? parseJSON(modeSettingsData.summerStart) : undefined, // date
    summerEnd: modeSettingsData.summerEnd ? parseJSON(modeSettingsData.summerEnd) : undefined, // date
    summerOffset: modeSettingsData.summerOffset ?? 0, // int
    //
    dstDayOfWeek: modeSettingsData.dstDayOfWeek ?? DstDayOfWeekType.OnExactDates,
    dstCallFunctionEnabled: Boolean(modeSettingsData.dstCallFunctionEnabled),
    dstConsumptionEnabled: Boolean(modeSettingsData.dstConsumptionEnabled),

    // learning //
    learningDataLimit: modeSettingsData.learningDataLimit ?? undefined,
  } as SettingsFormModel;
};

export const parseMinsFromMidnightToTime = (minsFromMidnight: number): string => {
  if (!Number.isFinite(minsFromMidnight) || minsFromMidnight === null) return undefined;
  const hours = minutesToHours(minsFromMidnight);
  const mins = minsFromMidnight - hours * 60;
  return `${(hours < 10 && '0') + hours}:${(mins < 10 && '0') + mins}`;
};

export const parseTimeToMinsFromMidnight = (time: string): number => {
  if (!time) return null;
  const [hours, mins] = time?.split(':');
  return Number(hours) * 60 + Number(mins);
};

export const parseAdvancedTablesFromGet = (
  apiAdvancedTable: PegasusAdvancedTimeAPIModel[],
  apiHolidaysAdvancedTable: PegasusAdvancedTimeHolidayAPIModel[]
) => {
  const advancedTable: PegasusAdvancedTimeModel[] = apiAdvancedTable.map((el) => {
    const time = `${(el.time.hours < 10 && '0') + el.time.hours}:${(el.time.minutes < 10 && '0') + el.time.minutes}`;
    return {
      ...el,
      time,
      pressure: el.pressure?.toString(),
      uiPressure: el.pressure?.toFixed(2),
    };
  });

  const advancedHolidayTable: PegasusAdvancedTimeHolidayModel[] = apiHolidaysAdvancedTable.map((el) => {
    const time = `${(el.time.hours < 10 && '0') + el.time.hours}:${(el.time.minutes < 10 && '0') + el.time.minutes}`;
    const date = el.date ? parseJSON(el.date) : null;
    return {
      ...el,
      date,
      time,
      pressure: el.pressure?.toString(),
      uiPressure: el.pressure?.toFixed(2),
    };
  });

  return { advancedTable, advancedHolidayTable };
};

export const parseAdvancedTablesToPost = (
  advancedTable: PegasusAdvancedTimeModel[],
  advancedHolidayTable: PegasusAdvancedTimeHolidayModel[]
) => {
  const apiAdvancedTable: PegasusAdvancedTimeAPIModel[] = advancedTable.map((el) => {
    const [hours, minutes] = el.time.split(':');
    return {
      ...el,
      time: { hours: Number(hours), minutes: Number(minutes) },
      pressure: Number(el.pressure),
    };
  });

  const apiHolidaysAdvancedTable: PegasusAdvancedTimeHolidayAPIModel[] = advancedHolidayTable.map((el) => {
    const [hours, minutes] = el.time.split(':');
    return {
      ...el,
      date: format(el.date, 'yyyy-MM-dd'),
      time: { hours: Number(hours), minutes: Number(minutes) },
      pressure: Number(el.pressure),
    };
  });

  return { apiAdvancedTable, apiHolidaysAdvancedTable };
};

export const parseMultipleOffsetTableFromGet = (apiMultipleOffsetTable: MultipleFlowTableOffsetsAPIModel[]) => {
  const multipleOffsetTable: MultipleFlowTableOffsetsModel[] = apiMultipleOffsetTable.map((el) => {
    const time = `${(el.time.hours < 10 && '0') + el.time.hours}:${(el.time.minutes < 10 && '0') + el.time.minutes}`;
    return {
      ...el,
      time,
    };
  });
  return multipleOffsetTable;
};

export const parseMultipleOffsetTableToPost = (multipleOffsetTable: MultipleFlowTableOffsetsModel[]) => {
  const apiMultipleOffsetTable: MultipleFlowTableOffsetsAPIModel[] = multipleOffsetTable.map((el) => {
    const [hours, minutes] = el.time.split(':');
    return {
      ...el,
      time: { hours: Number(hours), minutes: Number(minutes) },
    };
  });
  return apiMultipleOffsetTable;
};

export const parseTimeTableFromGet = (apiTimeTable: PegasusTimeStatusesAPIModel[]) => {
  const timeTable: PegasusTimeStatusesModel[] = apiTimeTable?.map((el) => {
    const time = `${(el.time.hours < 10 && '0') + el.time.hours}:${(el.time.minutes < 10 && '0') + el.time.minutes}`;
    return {
      ...el,
      time,
      pressure: el.pressure?.toString(),
      uiPressure: el.pressure?.toFixed(2),
    };
  });
  return timeTable;
};

export const parseTimeTableToPost = (timeTable: PegasusTimeStatusesModel[]) => {
  const apiTimeTable: PegasusTimeStatusesAPIModel[] = timeTable?.map((el) => {
    const [hours, minutes] = el.time.split(':');
    return {
      pressure: Number(el.pressure),
      timeType: el.timeType,
      time: { hours: Number(hours), minutes: Number(minutes) },
    };
  });
  return apiTimeTable;
};

export const parseFlowTableFromGet = (apiFlowTable: PegasusFlowStatusesAPIModel[]) => {
  const flowTable: PegasusFlowStatusesModel[] = apiFlowTable?.map((el) => {
    return {
      ...el,
      flow: el.flow?.toString(),
      pressure: el.pressure?.toString(),
      uiFlow: el.flow?.toFixed(2),
      uiPressure: el.pressure?.toFixed(2),
    };
  });
  return flowTable;
};

export const parseFlowTableToPost = (flowTable: PegasusFlowStatusesModel[]) => {
  const apiFlowTable: PegasusFlowStatusesAPIModel[] = flowTable?.map((el) => {
    return {
      ...el,
      flow: Number(el.flow),
      pressure: Number(el.pressure),
    };
  });
  return apiFlowTable;
};

export const parsePegasusPredefinedTablesFromGet = (
  tables: PegasusPredefinedTablesAPIModel
): PegasusPredefinedTablesModel => {
  if (!tables || isEmpty(tables)) {
    return { flow: [], time: [] };
  }

  const flowTables =
    tables.pegasusFlowStatuses?.map((table) => ({
      ...table,
      data: table.data.map((el) => ({
        ...el,
        flow: el.flow?.toString(),
        pressure: el.pressure?.toString(),
        uiFlow: el.flow?.toFixed(2),
        uiPressure: el.pressure?.toFixed(2),
      })),
    })) ?? [];

  const timeTables =
    tables.pegasusTimeStatuses?.map((table) => ({
      ...table,
      data: table.data.map((el) => {
        return {
          ...el,
          pressure: el.pressure?.toString(),
          uiPressure: el.pressure?.toFixed(2),
        };
      }),
    })) ?? [];

  return { flow: flowTables, time: timeTables };
};

export const parseFlowPredefinedTableToPut = (
  table: PegasusPredefinedSingleTablesModel<PegasusFlowStatusesModel>,
  units: PMZoneUnitsState
): PegasusPredefinedFlowPutModel => {
  if (!table || isEmpty(table) || !table.data || isEmpty(table.data)) {
    return null;
  }

  return {
    tableId: table.id ?? null,
    tableName: table.name,
    flowTable: table.data.map((el) => ({
      ...el,
      flow: Number(el.flow),
      pressure: Number(el.pressure),
    })),
    pressureUnits: units.pressureUnit,
    flowUnits: units.flowUnit,
  };
};

export const parseTimePredefinedTableToPut = (
  table: PegasusPredefinedSingleTablesModel<PegasusTimeStatusesModel>,
  units: PMZoneUnitsState
): PegasusPredefinedTimePutModel => {
  if (!table || isEmpty(table) || !table.data || isEmpty(table.data)) {
    return null;
  }

  return {
    tableId: table.id ?? null,
    tableName: table.name,
    timeTable: table.data.map((el) => {
      const [hours, minutes] = el.time.split(':');

      return {
        pressure: Number(el.pressure),
        timeType: el.timeType,
        time: { hours: Number(hours), minutes: Number(minutes) },
      };
    }),
    pressureUnits: units.pressureUnit,
  };
};

export const displayErrorMessages = (
  errorArray: SettingsFormErrorModel,
  handleError: (error: string, context?: string) => void,
  mode: ModeSettingsTabsType
) => {
  errorArray?.data?.forEach((el) => {
    el.errors?.forEach((err) => {
      const differentCode = err.code === ErrorCodeFromApi.InsufficientTimeTableEntries && mode === 'Active';

      const errMsg = differentCode
        ? t`There should be at least 672 rows provided in Advanced Tables in Active mode`
        : el.propertyName === 'FaultPressure'
          ? t`Fixed Outlet Value should be within actuator limits`
          : ErrorMsgToDisplayFn()[err.code];

      const property = el.propertyName ? ErrorPropertyToDisplayFn()[el.propertyName] : t`Settings form`;
      handleError(errMsg, property);
    });
  });
};

// validation schema in YUP

const valueRequired = (name: string) => `${name} ${t`value is required`}`;
const valueInteger = (name: string) => `${name} ${t`value should be an integer`}`;
const valueNumber = (name: string) => `${name} ${t`value should be a number`}`;
const valueGreaterEquals = (name: string, val: number | string, notEqual?: boolean) =>
  `${name} ${t`value should be greater`} ${notEqual ? t`than` : t`or equals`} ${val}`;
const valueLessEquals = (name: string, val: number | string, notEqual?: boolean) =>
  `${name} ${t`value should be less`} ${notEqual ? t`than` : t`or equals`} ${val}`;

export const learningOrActiveMode = (mode: ModeSettingsTabsType) =>
  mode === ModeSettingsTabsType.Learning || mode === ModeSettingsTabsType.Active;

export const flowOrFlowAndTimeTableMode = (mode: ModeSettingsTabsType) =>
  mode === ModeSettingsTabsType.ManualFlowTable || mode === ModeSettingsTabsType.ManualFlowAndTimeTable;

export const timeOrFlowAndTimeTableMode = (mode: ModeSettingsTabsType) =>
  mode === ModeSettingsTabsType.ManualTimeTable || mode === ModeSettingsTabsType.ManualFlowAndTimeTable;

export const getValidationSchema = (
  tablesData: TablesAndLearningDataSource,
  showMultipleCps: boolean,
  isPEG2Device: boolean
) => {
  const { timeTable, flowTable, advanced, advancedHoliday, tableType, offsetsTable } = tablesData;

  return object().shape(
    {
      cpChannelId: number().when('cpId', {
        is: (cpId: number) => Number.isFinite(cpId),
        then: number()
          .required(valueRequired(t`CP1 channel`))
          .nullable(),
        otherwise: number().notRequired().nullable(),
      }),

      cp2ChannelId: number().when(['cp2Id', 'cpMode'], {
        is: (cp2Id: number, cpMode: MultipleCPsType) =>
          Number.isFinite(cp2Id) && cpMode !== MultipleCPsType.Off && showMultipleCps,
        then: number()
          .required(valueRequired(t`CP2 channel`))
          .nullable(),
        otherwise: number().notRequired().nullable(),
      }),

      cp3ChannelId: number().when(['cp3Id', 'cpMode'], {
        is: (cp3Id: number, cpMode: MultipleCPsType) =>
          Number.isFinite(cp3Id) && cpMode === MultipleCPsType.Min3Cp && showMultipleCps,
        then: number()
          .required(valueRequired(t`CP3 channel`))
          .nullable(),
        otherwise: number().notRequired().nullable(),
      }),

      // - - - - GENERAL SETTINGS - - - -
      // DEADBAND
      deadBand: number().when('deadBandLimit', {
        is: (deadBandLimit: number) => deadBandLimit,
        then: number()
          .required(valueRequired(t`Deadband`))
          .typeError(valueNumber(t`Deadband`))
          .min(0, valueGreaterEquals(t`Deadband`, 0))
          .max(ref('deadBandLimit'), ({ max }) => valueLessEquals(t`Deadband`, max ?? 'Auto limit')),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Deadband`)),
      }),

      deadBandLimit: number().when('deadBand', {
        is: (deadBand: number) => deadBand,
        then: number()
          .required(valueRequired(t`Auto Limit`))
          .typeError(valueNumber(t`Auto Limit`))
          .moreThan(0, valueGreaterEquals(t`Auto Limit`, 0, true)),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Auto Limit`)),
      }),

      //ACTUATOR
      actuatorMin: number()
        .when('actuatorMax', {
          is: (actuatorMax: number) => actuatorMax,
          then: number()
            .required(valueRequired(t`Actuator minimum`))
            .typeError(valueNumber(t`Actuator minimum`))
            .min(0, valueGreaterEquals(t`Actuator minimum`, 0))
            .lessThan(ref('actuatorMax'), ({ less }) => valueLessEquals(t`Actuator minimum`, less ?? 'Maximum', true)),
          otherwise: number()
            .notRequired()
            .typeError(valueNumber(t`Actuator minimum`)),
        })
        .when('controlMode', {
          is: (controlMode: ModeSettingsTabsType) =>
            timeOrFlowAndTimeTableMode(controlMode) || flowOrFlowAndTimeTableMode(controlMode),
          then: (schema: any) =>
            schema.test(
              'is-lowest-entry-in-time-table-lower-tha',
              t`Pressure values in table must be be in actuator limits`,
              (actuatorMin: string, context: any) => {
                if (actuatorMin === null || actuatorMin === '' || actuatorMin === undefined) {
                  return true;
                }

                const controlMode = context?.parent?.controlMode as ModeSettingsTabsType;

                // only for time
                const type = tableType.data;

                // only for time & flow
                const controlFlags = context?.parent?.controlFlags as ControlFlagsType;

                const tableToValidate =
                  (controlMode === ModeSettingsTabsType.ManualFlowTable
                    ? flowTable.data
                    : controlMode === ModeSettingsTabsType.ManualTimeTable
                      ? type === TimeLearningTableType.Regular
                        ? timeTable.data
                        : [...(advanced.data ?? []), ...(advancedHoliday.data ?? [])]
                      : [
                          ...flowTable.data,
                          ...(controlFlags !== ControlFlagsType.DontUseTimeTable ? timeTable.data : []),
                        ]) ?? [];

                return Math.min(...tableToValidate.map((entry) => Number(entry.pressure))) >= Number(actuatorMin);
              }
            ),
        }),
      actuatorMax: number()
        .when('actuatorMin', {
          is: (actuatorMin: number) => actuatorMin,
          then: number()
            .required(valueRequired(t`Actuator maximum`))
            .typeError(valueNumber(t`Actuator maximum`))
            .moreThan(ref('actuatorMin'), ({ more }) =>
              valueGreaterEquals(t`Actuator maximum`, more ?? 'Minimum', true)
            ),
          otherwise: number()
            .notRequired()
            .typeError(valueNumber(t`Actuator maximum`)),
        })
        .when('controlMode', {
          is: (controlMode: ModeSettingsTabsType) =>
            timeOrFlowAndTimeTableMode(controlMode) || flowOrFlowAndTimeTableMode(controlMode),
          then: (schema: any) =>
            schema.test(
              'is-highest-entry-in-time-table-greater-than',
              t`Pressure values in table must be be in actuator limits`,
              (actuatorMax: string, context: any) => {
                if (actuatorMax === null || actuatorMax === '' || actuatorMax === undefined) {
                  return true;
                }

                const controlMode = context?.parent?.controlMode as ModeSettingsTabsType;

                // only for time
                const type = tableType.data;

                // only for time & flow
                const controlFlags = context?.parent?.controlFlags as ControlFlagsType;

                const tableToValidate =
                  (controlMode === ModeSettingsTabsType.ManualFlowTable
                    ? flowTable.data
                    : controlMode === ModeSettingsTabsType.ManualTimeTable
                      ? type === TimeLearningTableType.Regular
                        ? timeTable.data
                        : [...(advanced.data ?? []), ...(advancedHoliday.data ?? [])]
                      : [
                          ...flowTable.data,
                          ...(controlFlags !== ControlFlagsType.DontUseTimeTable ? timeTable.data : []),
                        ]) ?? [];

                return Math.max(...tableToValidate.map((entry) => Number(entry.pressure))) <= Number(actuatorMax);
              }
            ),
        }),

      //SINGLE FLOW TABLE OFFSET
      nightOffset: number()
        .notRequired()
        .typeError(valueNumber(t`Offset Pressure`)),
      nightStartMins: string().when(['nightEndMins', 'controlMode'], {
        is: (nightEndMins: string, controlMode: ModeSettingsTabsType) =>
          nightEndMins &&
          (isEmpty(offsetsTable) ||
            !(
              flowOrFlowAndTimeTableMode(controlMode) ||
              (learningOrActiveMode(controlMode) && tableType.data === TimeLearningTableType.Regular)
            )),
        then: string().required(valueRequired(t`Between Time`)),
        otherwise: string().notRequired().nullable(),
      }),
      nightEndMins: string().when(['nightStartMins', 'controlMode'], {
        is: (nightStartMins: string, controlMode: ModeSettingsTabsType) =>
          nightStartMins &&
          (isEmpty(offsetsTable) ||
            !(
              flowOrFlowAndTimeTableMode(controlMode) ||
              (learningOrActiveMode(controlMode) && tableType.data === TimeLearningTableType.Regular)
            )),
        then: string().required(valueRequired(t`And Time`)),
        otherwise: string().notRequired().nullable(),
      }),

      //SOLENOID
      solenoidActuationThreshold: number()
        .notRequired()
        .typeError(valueNumber(t`Solenoid Actuations Threshold`))
        .min(0, valueGreaterEquals(t`Solenoid Actuations Threshold`, 0))
        .integer(valueInteger(t`Solenoid Actuations Threshold`)),

      //CONTROLLER ALARMS
      minInletPres: number().when('maxInletPres', {
        is: (maxInletPres: number) => maxInletPres,
        then: number()
          .typeError(valueNumber(t`Inlet Pressure Lower`))
          .min(0, valueGreaterEquals(t`Inlet Pressure Lower`, 0))
          .lessThan(ref('maxInletPres'), ({ less }) =>
            valueGreaterEquals(t`Inlet Pressure Lower`, less ?? 'Inlet Pressure Upper', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Inlet Pressure Lower`)),
      }),
      maxInletPres: number().when('minInletPres', {
        is: (minInletPres: number) => minInletPres,
        then: number()
          .typeError(valueNumber(t`Inlet Pressure Upper`))
          .moreThan(ref('minInletPres'), ({ more }) =>
            valueGreaterEquals(t`Inlet Pressure Upper`, more ?? 'Inlet Pressure Lower', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Inlet Pressure Upper`)),
      }),
      minFlow: number().when('maxFlow', {
        is: (maxFlow: number) => maxFlow,
        then: number()
          .typeError(valueNumber(t`Flow Lower`))
          .min(0, valueGreaterEquals(t`Flow Lower`, 0))
          .lessThan(ref('maxFlow'), ({ less }) => valueGreaterEquals(t`Flow Lower`, less ?? 'Flow Upper', true)),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Flow Lower`)),
      }),
      maxFlow: number().when('minFlow', {
        is: (minFlow: number) => minFlow,
        then: number()
          .typeError(valueNumber(t`Flow Upper`))
          .moreThan(ref('minFlow'), ({ more }) => valueGreaterEquals(t`Flow Upper`, more ?? 'Flow Lower', true)),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Flow Upper`)),
      }),
      minOutletPres: number().when('maxOutletPres', {
        is: (maxOutletPres: number) => maxOutletPres,
        then: number()
          .typeError(valueNumber(t`Outlet Pressure Lower`))
          .min(0, valueGreaterEquals(t`Outlet Pressure Lower`, 0))
          .lessThan(ref('maxOutletPres'), ({ less }) =>
            valueGreaterEquals(t`Outlet Pressure Lower`, less ?? 'Outlet Pressure Upper', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Outlet Pressure Lower`)),
      }),
      maxOutletPres: number().when('minOutletPres', {
        is: (minOutletPres: number) => minOutletPres,
        then: number()
          .typeError(valueNumber(t`Outlet Pressure Upper`))
          .moreThan(ref('minOutletPres'), ({ more }) =>
            valueGreaterEquals(t`Outlet Pressure Upper`, more ?? 'Outlet Pressure Lower', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Outlet Pressure Upper`)),
      }),
      minTopPres: number().when('maxTopPres', {
        is: (maxTopPres: number) => maxTopPres,
        then: number()
          .typeError(valueNumber(t`Top Pressure Lower`))
          .min(0, valueGreaterEquals(t`Top Pressure Lower`, 0))
          .lessThan(ref('maxTopPres'), ({ less }) =>
            valueGreaterEquals(t`Top Pressure Lower`, less ?? 'Top Pressure Upper', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Top Pressure Lower`)),
      }),
      maxTopPres: number().when('minTopPres', {
        is: (minTopPres: number) => minTopPres,
        then: number()
          .typeError(valueNumber(t`Top Pressure Upper`))
          .moreThan(ref('minTopPres'), ({ more }) =>
            valueGreaterEquals(t`Top Pressure Upper`, more ?? 'Top Pressure Lower', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Top Pressure Upper`)),
      }),

      //CP CONTROL
      cpMin: number().when(['minValidCp', 'maxValidCp'], {
        is: (minValidCp: number, maxValidCp: number) => minValidCp && maxValidCp,
        then: number()
          .required(valueRequired(t`Target`))
          .typeError(valueNumber(t`Target`))
          .min(ref('minValidCp'), ({ min }) => valueGreaterEquals(t`Target`, min))
          .max(ref('maxValidCp'), ({ max }) => valueLessEquals(t`Target`, max)),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Target`)),
      }),
      cpBand: number()
        .notRequired()
        .typeError(
          valueNumber(
            t({ message: 'Send Alarm if', id: '[CPControl.SendAlarmIf]' }) +
              ' +/- [] ' +
              t({ message: 'meters from target', id: '[CPControl.MetersFromTarget]' })
          )
        )
        .min(
          0,
          valueGreaterEquals(
            t({ message: 'Send Alarm if', id: '[CPControl.SendAlarmIf]' }) +
              ' +/- [] ' +
              t({ message: 'meters from target', id: '[CPControl.MetersFromTarget]' }),
            0
          )
        ),
      minValidCp: number().when(['controlMode', 'maxValidCp', 'cpMin'], {
        is: (controlMode: ModeSettingsTabsType, maxValidCp: number, cpMin: number) =>
          learningOrActiveMode(controlMode) && (maxValidCp || cpMin),
        then: number()
          .required(valueRequired(t`CP Minimum`))
          .typeError(valueNumber(t`CP Minimum`))
          .min(0, valueGreaterEquals(t`CP Minimum`, 0))
          .lessThan(ref('maxValidCp'), ({ less }) =>
            valueLessEquals(t`CP Minimum`, less ?? 'Maximum CP valid pressure', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`CP Minimum`)),
      }),
      maxValidCp: number().when(['controlMode', 'minValidCp', 'cpMin'], {
        is: (controlMode: ModeSettingsTabsType, minValidCp: number, cpMin: number) =>
          learningOrActiveMode(controlMode) && (minValidCp || cpMin),
        then: number()
          .required(valueRequired(t`CP Maximum`))
          .typeError(valueNumber(t`CP Maximum`))
          .moreThan(ref('minValidCp'), ({ more }) =>
            valueGreaterEquals(t`CP Maximum`, more ?? 'Minimum CP valid pressure', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`CP Maximum`)),
      }),
      cpNightStartMins: number().when('cpNightEndMins', {
        is: (cpNightEndMins: number) => cpNightEndMins,
        then: number()
          .required(valueRequired(t`CP between`))
          .typeError(valueNumber(t`CP between`))
          .min(0, valueGreaterEquals(t`CP between`, 0))
          .lessThan(ref('cpNightEndMins'), ({ less }) => valueLessEquals(t`CP between`, less, true)),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`CP between`)),
      }),
      cpNightEndMins: number().when('cpNightStartMins', {
        is: (cpNightStartMins: number) => cpNightStartMins,
        then: number()
          .required(valueRequired(t`CP and`))
          .typeError(valueNumber(t`CP and`))
          .moreThan(ref('cpNightStartMins'), ({ more }) => valueGreaterEquals(t`CP and`, more, true)),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`CP and`)),
      }),

      // - - - - TARGET OUTLET PRESS - - - -
      cpTarget: number().when('controlMode', {
        is: (controlMode: ModeSettingsTabsType) => controlMode === 'ManualFixedOutlet',
        then: number()
          .required(valueRequired(t`Target Outlet Pressure`))
          .typeError(valueNumber(t`Target Outlet Pressure`))
          .min(0, valueGreaterEquals(t`Target Outlet Pressure`, 0)),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Target Outlet Pressure`)),
      }),

      // - - - - MODE SETTINGS - - - -
      // summer time offset
      summerOffset: number()
        .notRequired()
        .typeError(valueNumber(t`Adjust by`))
        .min(0, valueGreaterEquals(t`Adjust by`, 0)),
      summerStart: date().when(['summerOffset', 'controlMode'], {
        is: (summerOffset: number, controlMode: ModeSettingsTabsType) =>
          (timeOrFlowAndTimeTableMode(controlMode) || learningOrActiveMode(controlMode)) && summerOffset !== 0,
        then: date()
          .required(valueRequired(isPEG2Device ? t`DST Start` : t`From Sunday After`))
          .typeError('Value should be a date'),
        otherwise: date().notRequired().nullable(),
      }),
      summerEnd: date().when(['summerOffset', 'controlMode'], {
        is: (summerOffset: number, controlMode: ModeSettingsTabsType) =>
          (timeOrFlowAndTimeTableMode(controlMode) || learningOrActiveMode(controlMode)) && summerOffset !== 0,
        then: date()
          .required(valueRequired(isPEG2Device ? t`DST End` : t`To Sunday After`))
          .typeError('Value should be a date'),
        otherwise: date().notRequired().nullable(),
      }),

      //learning
      learningDataLimit: number().when('controlMode', {
        is: (controlMode: ModeSettingsTabsType) => learningOrActiveMode(controlMode),
        then: number()
          .required(valueRequired(t`Learning Data Limit`))
          .typeError(valueNumber(t`Learning Data Limit`))
          .integer(valueInteger(t`Learning Data Limit`))
          .min(50, valueGreaterEquals(t`Learning Data Limit`, 50))
          .max(100, valueLessEquals(t`Learning Data Limit`, 100)),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Learning Data Limit`)),
      }),

      //invalid flows //
      useZeroFlows: boolean(),
      faultTimeout: number().when(['useZeroFlows', 'controlMode'], {
        is: (useZeroFlows: boolean, controlMode: ModeSettingsTabsType) =>
          (flowOrFlowAndTimeTableMode(controlMode) ||
            (learningOrActiveMode(controlMode) && tableType.data === TimeLearningTableType.Regular)) &&
          useZeroFlows,
        then: number()
          .required(valueRequired(t`Duration [minutes]`))
          .typeError(valueNumber(t`Duration [minutes]`))
          .min(0, valueGreaterEquals(t`Duration [minutes]`, 0)),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Duration [minutes]`)),
      }),
      useFlowBandLimits: boolean(),
      minValidFlow: number().when(['useFlowBandLimits', 'maxValidFlow', 'controlMode'], {
        is: (useFlowBandLimits: boolean, maxValidFlow: number, controlMode: ModeSettingsTabsType) =>
          (flowOrFlowAndTimeTableMode(controlMode) ||
            (learningOrActiveMode(controlMode) && tableType.data === TimeLearningTableType.Regular)) &&
          useFlowBandLimits &&
          maxValidFlow,
        then: number()
          .required(valueRequired(t`Invalid Flows Minimum`))
          .typeError(valueNumber(t`Invalid Flows Minimum`))
          .min(0, valueGreaterEquals(t`Invalid Flows Minimum`, 0))
          .lessThan(ref('maxValidFlow'), ({ less }) =>
            valueLessEquals(t`Invalid Flows Minimum`, less ?? 'Maximum', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Invalid Flows Minimum`)),
      }),
      maxValidFlow: number().when(['useFlowBandLimits', 'minValidFlow', 'controlMode'], {
        is: (useFlowBandLimits: boolean, minValidFlow: number, controlMode: ModeSettingsTabsType) =>
          (flowOrFlowAndTimeTableMode(controlMode) ||
            (learningOrActiveMode(controlMode) && tableType.data === TimeLearningTableType.Regular)) &&
          useFlowBandLimits &&
          minValidFlow,
        then: number()
          .required(valueRequired(t`Invalid Flows Maximum`))
          .typeError(valueNumber(t`Invalid Flows Maximum`))
          .moreThan(ref('minValidFlow'), ({ more }) =>
            valueGreaterEquals(t`Invalid Flows Maximum`, more ?? 'Minimum', true)
          ),
        otherwise: number()
          .notRequired()
          .typeError(valueNumber(t`Invalid Flows Maximum`)),
      }),
      faultPressure: number()
        .when(['useZeroFlows', 'useFlowBandLimits', 'invalidFlowOverride', 'controlMode'], {
          is: (
            useZeroFlows: boolean,
            useFlowBandLimits: boolean,
            invalidFlowOverride: FlowInvalidToUseType,
            controlMode: ModeSettingsTabsType
          ) =>
            (flowOrFlowAndTimeTableMode(controlMode) ||
              (learningOrActiveMode(controlMode) && tableType.data === TimeLearningTableType.Regular)) &&
            (useZeroFlows || useFlowBandLimits) &&
            invalidFlowOverride === 'UseFixedOutlet',
          then: number()
            .required(valueRequired(t`Fixed Outlet`))
            .typeError(valueNumber(t`Fixed Outlet`))
            .min(0, valueGreaterEquals(t`Fixed Outlet`, 0)),
          otherwise: number()
            .notRequired()
            .typeError(valueNumber(t`Fixed Outlet`)),
        })
        .when(
          ['actuatorMin', 'actuatorMax', 'useZeroFlows', 'useFlowBandLimits', 'invalidFlowOverride', 'controlModel'],
          {
            is: (
              actuatorMin: number,
              actuatorMax: number,
              useZeroFlows: boolean,
              useFlowBandLimits: boolean,
              invalidFlowOverride: FlowInvalidToUseType,
              controlMode: ModeSettingsTabsType
            ) =>
              (flowOrFlowAndTimeTableMode(controlMode) ||
                (learningOrActiveMode(controlMode) && tableType.data === TimeLearningTableType.Regular)) &&
              (useZeroFlows || useFlowBandLimits) &&
              invalidFlowOverride === 'UseFixedOutlet' &&
              actuatorMin >= 0 &&
              actuatorMax,
            then: number()
              .required(valueRequired(t`Fixed Outlet`))
              .typeError(valueNumber(t`Fixed Outlet`))
              .min(ref('actuatorMin'), ({ min }) => valueGreaterEquals(t`Fixed Outlet`, min))
              .max(ref('actuatorMax'), ({ max }) => valueLessEquals(t`Fixed Outlet`, max)),
            otherwise: number()
              .notRequired()
              .typeError(valueNumber(t`Fixed Outlet`)),
          }
        ),
    },
    [
      ['cpTarget', 'controlMode'],
      ['minValidCp', 'cpMin'],
      ['maxValidCp', 'cpMin'],
      ['minValidCp', 'maxValidCp'],
      ['cpNightStartMins', 'cpNightEndMins'],
      ['deadBand', 'deadBandLimit'],
      ['actuatorMin', 'actuatorMax'],
      ['nightStartMins', 'nightEndMins'],
      ['minInletPres', 'maxInletPres'],
      ['minFlow', 'maxFlow'],
      ['minOutletPres', 'maxOutletPres'],
      ['minTopPres', 'maxTopPres'],
      ['summerStart', 'summerEnd'],
      ['minValidFlow', 'maxValidFlow'],
      ['actuatorMin', 'timeTable'],
    ]
  );
};
